/**
 * "Authorized Exploitation" capabilities (Internal Testing page) — targeted, safe confirmation
 * probes against already-reported findings, distinct from the discovery-phase scans in
 * apps/kali/src/tools.ts. Single source of truth shared between InternalTesting.tsx (the
 * capability list) and FindingDetail.tsx (labeling which capability the "Confirm finding" button
 * would exercise for a given finding).
 *
 * TODO: none of these are dispatched yet — FindingDetail.tsx's "Confirm finding" button still only
 * calls the single generic `findingsApi.confirm()` (apps/worker/src/jobs/confirmation-proof-handler.ts),
 * which does not yet route per-capability. Wiring a real per-capability confirmation job is a
 * follow-up; this module only labels which capability WOULD apply, for now.
 */

export type ExploitationCapabilityStatus = 'available' | 'requires_credentials';

export interface ExploitationCapability {
  id: string;
  name: string;
  description: string;
  requires: string;
  status: ExploitationCapabilityStatus;
  /** moduleId substrings this capability applies to (see matchExploitationCapability below). */
  moduleIdMatches: string[];
}

export const EXPLOITATION_CAPABILITIES: ExploitationCapability[] = [
  {
    id: 'default-credentials',
    name: 'Default credential testing',
    description: 'SSH, FTP, databases — 3 attempts max, lockout-aware',
    requires: 'A service with a known default-credential set detected',
    status: 'available',
    moduleIdMatches: ['credential'],
  },
  {
    id: 'smb-null-session',
    name: 'SMB null session access confirmation',
    description: 'Confirms whether SMB shares are reachable with no credentials',
    requires: 'An SMB service detected on the target',
    status: 'available',
    moduleIdMatches: ['enum4linux'],
  },
  {
    id: 'ldap-anonymous-bind',
    name: 'Anonymous LDAP bind confirmation',
    description: 'Confirms whether the directory allows an unauthenticated bind',
    requires: 'An LDAP/AD service detected on the target',
    status: 'available',
    moduleIdMatches: ['ldapdomaindump'],
  },
  {
    id: 'service-vulnerability',
    name: 'Service vulnerability confirmation via safe probes',
    description: 'Re-validates a reported vulnerability with a targeted, non-destructive probe',
    requires: 'A finding with a known CVE or vulnerability signature',
    status: 'available',
    moduleIdMatches: ['nuclei', 'nikto'],
  },
];

/** Finds which (if any) Authorized Exploitation capability a finding's moduleId corresponds to —
 * used purely to label the existing "Confirm finding" button, not to select a dispatch path. */
export function matchExploitationCapability(moduleId: string): ExploitationCapability | null {
  return EXPLOITATION_CAPABILITIES.find((capability) => capability.moduleIdMatches.some((fragment) => moduleId.includes(fragment))) ?? null;
}

/** The inverse of matchExploitationCapability — every OPEN finding a given capability's "Run"
 * dialog (InternalTesting.tsx) should offer in its finding-picker dropdown. */
export function findingsForCapability<T extends { moduleId: string; status: string }>(capability: ExploitationCapability, findings: T[]): T[] {
  return findings.filter((f) => f.status === 'open' && capability.moduleIdMatches.some((fragment) => f.moduleId.includes(fragment)));
}
